/**
 * Accessibility: announce cart status messages via a single, persistent live region.
 *
 * The region markup lives in the theme layouts (#cart-live-region). It follows
 * focus into whichever modal is open, because the cart drawer is an aria-modal
 * dialog and screen readers prune everything outside an open modal from the
 * accessibility tree — a live region left on the body would be silent for as
 * long as the drawer is open.
 *
 * "Added to cart" does not go through the live region when the cart drawer is
 * about to open. It is prefixed onto the drawer's aria-label instead, so it is
 * spoken as the first thing in the dialog's own announcement the moment the
 * drawer takes focus, and the label is put back when the drawer closes.
 *
 * A live region cannot win that race: the drawer opens 700ms after the item is
 * added, and the focus event discards any polite text still queued — which is
 * what made the confirmation drop out roughly one time in six. Holding it until
 * after the drawer had settled kept it, but it then landed behind the screen
 * reader's full read-out of the cart, far too late to be useful.
 *
 * The accessible name is used rather than aria-describedby, which was tried and
 * was silent: a description on a dialog container is read late or not at all
 * depending on the screen reader and its verbosity settings, and the element it
 * pointed at had to live outside the drawer to survive the cart re-render —
 * which is precisely the part of the page an aria-modal dialog prunes. A label
 * carries its text inline, so there is no reference for the pruning to break.
 */
(() => {
  const REGION_ID = 'cart-live-region';

  // Several events can describe one cart action (e.g. the early announcement
  // from product-form plus the on:cart:add that follows it). Repeats of the
  // same message are collapsed while one is waiting to be written, and for this
  // long after one has been written.
  const DUPLICATE_WINDOW = 1000;

  // One add raises several events (product-form's early on:cart:announce, then
  // the on:cart:add that follows its second cart fetch). The first one picks how
  // the confirmation is delivered; the rest are ignored for this long.
  const ADD_WINDOW = 2500;

  // How long the drawer label waits to be read by a drawer opening. If the
  // drawer never opens, nothing will speak it and the live region takes over.
  const LABEL_TIMEOUT = 2000;

  // An already-open drawer re-renders its own contents ~700ms after the item is
  // added, which destroys the live region while it is hosted in there. Nothing
  // takes focus in that case, so the message just has to outwait the render.
  const DRAWER_RENDER_TIMEOUT = 1000;

  // Pause after the drawer has opened, so the message is queued behind the
  // dialog announcement rather than competing with it.
  const SETTLE_DELAY = 300;

  // Gap between clearing the region and writing to it, so the same message
  // twice in a row is still re-read. Longer when the region has just been
  // created or moved, to give assistive tech time to register it first.
  const WRITE_DELAY = 100;
  const NEW_HOST_WRITE_DELAY = 250;

  // A message younger than this may not have been read out yet, so it is
  // written again if the region has to move into a different modal.
  const REWRITE_WINDOW = 2500;

  // Text is wiped from the region once it has had time to be read. It is hidden
  // but still in the accessibility tree, and gets parked inside the open drawer
  // — anything left in it is read out all over again as part of the dialog's
  // contents.
  const CLEANUP_DELAY = 5000;

  const initialRegion = document.getElementById(REGION_ID);
  const addedText = (initialRegion && initialRegion.dataset.addedText)
    || 'Product added to your cart.';
  const errorText = (initialRegion && initialRegion.dataset.errorText)
    || (window.theme && theme.strings && theme.strings.cartError)
    || 'There was a problem adding this item to your cart.';

  let region = initialRegion;
  let regionMovedAt = 0;
  let lastMessage = '';
  let lastWrittenAt = 0;
  let pendingMessage = '';
  let holdTimer;
  let writeTimer;

  let labelledForAdd = false;
  let addHandledAt = 0;
  let labelTimer;
  let drawerLabel = '';

  /**
   * Returns the element the live region should currently sit in: the modal on
   * screen if there is one, otherwise the document body.
   *
   * Focus identifies the modal where it can, not a DOM query — several closed
   * modals (e.g. bundle "View details" pop-ups) keep aria-modal="true" in the
   * markup at all times, so a query alone would match one that is not on
   * screen. Focus is not always inside the open dialog though: a cart render
   * replaces the drawer's contents, which destroys the focused element and
   * drops focus to the body, so drawers are matched on their open state too.
   * @returns {Element}
   */
  function getRegionHost() {
    const active = document.activeElement;
    const focusedModal = active && typeof active.closest === 'function'
      ? active.closest('[aria-modal="true"]:not([aria-hidden="true"])')
      : null;

    return focusedModal
      || document.querySelector('[aria-modal="true"][aria-hidden="false"][open]')
      || document.body;
  }

  /**
   * Returns the live region, recreating it if a layout omitted it or a cart
   * render removed it, and re-homing it in the modal on screen if there is one.
   * @returns {Element}
   */
  function ensureRegion() {
    if (!region || !region.isConnected) {
      region = document.getElementById(REGION_ID);
    }

    if (!region) {
      region = document.createElement('div');
      region.id = REGION_ID;
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('aria-atomic', 'true');
      regionMovedAt = Date.now();
    }

    const host = getRegionHost();
    if (region.parentElement !== host) {
      // Moving the node takes it out of the accessibility tree and back in,
      // which loses any text not read out yet. Carry a recent message into the
      // new modal rather than letting it disappear. Moving out to the body is
      // the drawer closing, by which point the message has been read.
      const carried = host !== document.body && Date.now() - lastWrittenAt < REWRITE_WINDOW
        ? region.textContent
        : '';

      region.textContent = '';
      host.appendChild(region);
      regionMovedAt = Date.now();

      if (carried) {
        pendingMessage = carried;
        clearTimeout(holdTimer);
        holdTimer = setTimeout(flush, 0); // eslint-disable-line no-use-before-define
      }
    }

    return region;
  }

  /**
   * Writes a message to the live region. Clears first, then sets after a delay
   * so the same message announced twice in a row is still re-read.
   * @param {string} message - Text to announce.
   */
  function write(message) {
    clearTimeout(writeTimer);
    pendingMessage = '';
    lastWrittenAt = Date.now();

    // Clearing before ensureRegion serves double duty: it is the "clear, then
    // set" that makes a repeated message re-read, and it stops a re-home in
    // ensureRegion from treating this message as one still needing carrying.
    if (region) region.textContent = '';

    const el = ensureRegion();
    const delay = Date.now() - regionMovedAt < NEW_HOST_WRITE_DELAY
      ? NEW_HOST_WRITE_DELAY
      : WRITE_DELAY;

    writeTimer = setTimeout(() => {
      el.textContent = message;
      lastWrittenAt = Date.now();

      // Emptying it announces nothing, and keeps it from being read a second
      // time as content of whichever dialog it is parked in.
      writeTimer = setTimeout(() => { el.textContent = ''; }, CLEANUP_DELAY);
    }, delay);
  }

  /**
   * Writes the held message, if there is still one.
   */
  function flush() {
    clearTimeout(holdTimer);
    if (pendingMessage) write(pendingMessage);
  }

  /**
   * Returns how long a message should wait before being written to the region,
   * or 0 when nothing is about to disturb it.
   * @returns {number}
   */
  function drawerHold() {
    const drawer = document.querySelector('cart-drawer');

    return drawer && drawer.hasAttribute('open') ? DRAWER_RENDER_TIMEOUT : 0;
  }

  /**
   * Puts the drawer's own name back, so a later opening of the drawer that has
   * nothing to do with adding an item stays silent about it.
   */
  function restoreDrawerLabel() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && drawerLabel) drawer.setAttribute('aria-label', drawerLabel);

    clearTimeout(labelTimer);
    labelledForAdd = false;
  }

  /**
   * Prefixes a message onto the cart drawer's accessible name, to be spoken at
   * the front of the dialog announcement when the drawer opens and takes focus.
   *
   * The name is used rather than a description because it is the one part of a
   * dialog every screen reader announces on focus, and because aria-label holds
   * its text inline — there is no referenced element for the cart re-render to
   * destroy, nor for the modal to prune out of the accessibility tree.
   *
   * Only ever set while the drawer is closed, so changing the name of the
   * dialog cannot itself trigger an announcement.
   * @param {string} message - Text to announce.
   * @returns {boolean} False when no drawer is about to open to carry it.
   */
  function labelDrawer(message) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.hasAttribute('open')) return false;

    const settings = (window.theme && theme.settings) || {};
    if (settings.afterAtc !== 'drawer') return false;

    // Quick Add keeps its own drawer open and shows its own confirmation, so
    // the cart drawer stays shut and nothing takes focus away.
    if (document.querySelector('quick-add-drawer[aria-hidden="false"]')) return false;

    // Captured once, and only from a label that has not already been prefixed,
    // so repeated adds cannot stack the message up on itself.
    if (!drawerLabel) drawerLabel = drawer.getAttribute('aria-label') || '';
    if (!drawerLabel) return false;

    drawer.setAttribute('aria-label', `${message} ${drawerLabel}`);
    labelledForAdd = true;

    // Should the drawer fail to open after all, nothing reads the label, so
    // hand the message back to the live region.
    clearTimeout(labelTimer);
    labelTimer = setTimeout(() => {
      if (!labelledForAdd) return;
      restoreDrawerLabel();
      announce(message); // eslint-disable-line no-use-before-define
    }, LABEL_TIMEOUT);

    return true;
  }

  /**
   * Delivers the "added to cart" confirmation by whichever route reaches the
   * customer soonest, and only once per add however many events describe it.
   */
  function handleAdded() {
    if (labelledForAdd || Date.now() - addHandledAt < ADD_WINDOW) return;
    addHandledAt = Date.now();

    if (labelDrawer(addedText)) return;
    announce(addedText, true); // eslint-disable-line no-use-before-define
  }

  /**
   * True when a visible role="alert" already carries this text and will read it
   * out on its own. Add-to-cart failures and cart line item errors are shown
   * that way, so announcing them here as well reads them out twice.
   * @param {string} message - Text about to be announced.
   * @returns {boolean}
   */
  function shownAsAlert(message) {
    const text = message.trim();

    return Array.from(document.querySelectorAll('[role="alert"]')).some(
      (el) => el.textContent.trim() === text
        // An alert that is not rendered, or that sits in a subtree pruned from
        // the accessibility tree (a closed drawer), announces nothing.
        && (el.offsetWidth || el.offsetHeight || el.getClientRects().length)
        && !el.closest('[aria-hidden="true"]')
    );
  }

  /**
   * Queues a message for announcement.
   * @param {string} message - Text to announce.
   * @param {boolean} [waitForDrawer=false] - Hold until the cart UI has settled.
   */
  function announce(message, waitForDrawer = false) {
    if (!message) return;

    const isRepeat = message === lastMessage
      && (pendingMessage || Date.now() - lastWrittenAt < DUPLICATE_WINDOW);
    if (isRepeat) return;

    lastMessage = message;
    pendingMessage = message;

    clearTimeout(holdTimer);
    const hold = waitForDrawer ? drawerHold() : 0;
    if (!hold) {
      flush();
      return;
    }

    // Keep the region homed and in the DOM while the message waits, so the
    // write lands in a region assistive tech has already registered.
    ensureRegion();
    holdTimer = setTimeout(flush, hold);
  }

  // Generic announce channel. product-form fires this with status 'added' the
  // moment the item is added. Other cart UI (e.g. discount-code messages)
  // passes its own message, which nothing is about to interrupt.
  document.addEventListener('on:cart:announce', (evt) => {
    const detail = evt.detail || {};
    if (detail.status === 'added') {
      handleAdded();
    } else {
      announce(detail.message);
    }
  });

  // New line added to the cart (product form or Searchanise quick add).
  document.addEventListener('on:cart:add', handleAdded);

  // Adding an item already in the cart increments its quantity and fires
  // on:line-item:change from the product form instead of on:cart:add. Announce
  // those, but ignore quantity edits made inside the cart itself (cart-items).
  document.addEventListener('on:line-item:change', (evt) => {
    if (evt.target && typeof evt.target.closest === 'function' && evt.target.closest('product-form')) {
      handleAdded();
    }
  });

  // The drawer is about to take focus and have its name read out. If nothing
  // was just added, the confirmation is left over from an earlier add and must
  // not be read again.
  document.addEventListener('on:cart-drawer:before-open', () => {
    if (!labelledForAdd) {
      restoreDrawerLabel();
      return;
    }

    // Consumed by this opening: stop the fallback, but leave the label in place
    // until the drawer closes, so restoring it cannot race the screen reader
    // still building the announcement.
    clearTimeout(labelTimer);
    labelledForAdd = false;
  });

  // The drawer has been read and focus is on its way out, so the name can go
  // back to being just the drawer's own.
  document.addEventListener('on:cart-drawer:before-close', restoreDrawerLabel);

  // Add-to-cart failure (e.g. sold out, quantity limit). detail.error carries
  // the form's own message; fall back to a generic one. Nothing is announced
  // here when the form is already showing the message in a role="alert" — this
  // region only steps in when nothing else on the page will speak.
  document.addEventListener('on:cart:error', (evt) => {
    const message = (evt.detail && evt.detail.error) || '';
    if (message && shownAsAlert(message)) return;

    announce(message || errorText);
  });

  // The drawer has finished opening and taken focus, so a held message can now
  // be written into the region that moved in with it.
  document.addEventListener('on:cart-drawer:after-open', () => {
    if (!pendingMessage) return;
    clearTimeout(holdTimer);
    holdTimer = setTimeout(flush, SETTLE_DELAY);
  });

  // Opening or closing a drawer moves focus, so re-home the region as it moves.
  // Doing it here rather than only at announce time means the region is already
  // in place well before any text is written to it.
  document.addEventListener('focusin', ensureRegion);

  ensureRegion();
})();
